// Плеер сыгранной партии (профиль → проигранная партия): запись с сервера воспроизводится на настоящем игровом
// столе глазами проигравшего — по одному действию журнала, как ходят боты, с анимациями. Пауза, шаг назад/вперёд,
// переход по ходам, ползунок, скорость. Стол только для просмотра (ui.replay: act, save и боты выключены);
// партия, что была в st.G, и окно профиля возвращаются при выходе. Шаг назад — реплей от ближайшего снимка
// (снимки через каждые SNAP_EVERY действий), так что перемотка по длинной партии мгновенна.
import type { Game, GameRecord, PlayedGame, PlayerId } from '../types.ts';
import { createGame, applyRebalance, maxIid, setIidCounter } from '../engine/state.ts';
import { applyEntry, forkGame, gameFlags } from '../engine/game.ts';
import { replayRecordOutcome, recPlayers } from '../engine/record.ts';
import { reviewGame } from '../engine/review.ts';
import { rv, RULES_VERSION } from '../engine/rules.ts';
import type { ReplayInfo } from './board.ts';
import type { ReplayOp } from './commands.ts';
import type { Mode } from './state.ts';
import * as OV from './overlays.ts';
import type { ReplayEnd } from './overlays.ts';
import { st, ui, openModal, closeModal, repaintModal, modalOpen, currentModal, toast } from './store.ts';
import type { ModalBuild } from './store.ts';
import { render, showLog } from './render.ts';

type ViewRecord = Pick<GameRecord, 'seed' | 'players' | 'journal'> & Partial<GameRecord>;

/** мс на действие при скорости 1× (как шаг бота в анимационном режиме); решение по запросу — чуть дольше */
const STEP_MS = 800;
const SPEEDS = [1, 2, 4] as const;
const SNAP_EVERY = 20;

interface Replay {
  rec: ViewRecord;
  end: ReplayEnd;
  /** версия правил записи; до действия switchAt — from (партия продолжена после обновления правил) */
  version: number;
  from: number;
  switchAt: number;
  G: Game;
  pos: number;
  first: number;
  last: number;
  /** номер хода после pos действий */
  turns: number[];
  snaps: Map<number, Game>;
  playing: boolean;
  speed: number;
  timer: ReturnType<typeof setTimeout> | null;
  endBuild: ModalBuild;
  /** окно, из которого открыли (профиль), и что было на экране до плеера */
  back: ModalBuild | null;
  prev: { G: Game | null; viewer: PlayerId | null; lastSeq: number; mode: Mode; turnSeq: number | null };
}

let R: Replay | null = null;
let opening = 0;

/** Для панели плеера на столе (render.ts → GameScreen) */
export function replayInfo(): ReplayInfo | null {
  const r = R;
  if (!r) return null;
  return { pos: r.pos, first: r.first, last: r.last, turn: r.turns[r.pos] ?? 0, turns: r.turns[r.turns.length - 1] ?? 0, playing: r.playing, speed: r.speed };
}

/** Снимок позиции: копия с живым незавершённым эффектом (cloneGame его теряет — посреди запроса реплей споткнётся) */
const snap = (G: Game): Game => forkGame(G);

const versionAt = (r: Pick<Replay, 'version' | 'from' | 'switchAt'>, i: number): number => (i < r.switchAt ? r.from : r.version);

/** Применить действие №i журнала (на версии правил, действующей для него) */
function step(r: Pick<Replay, 'rec' | 'version' | 'from' | 'switchAt'>, G: Game, i: number): void {
  const want = versionAt(r, i);
  if (rv(G) !== want) {
    G.rulesVersion = want;
    applyRebalance(!!G.rebalance, want);
  }
  const e = r.rec.journal[i];
  if (!e) throw new Error(`нет записи ${i}`);
  const res = applyEntry(G, e);
  if (!res.ok) throw new Error(`запись ${i} (${e.a.name}): ${res.error}`);
}

/** Правила движка — обратно к партии на экране (реплей переключил цены и тексты карт на версию записи) */
function restoreRules(): void {
  const g = st.G;
  applyRebalance(!!g?.rebalance, g ? rv(g) : RULES_VERSION);
  if (g) setIidCounter(maxIid(g) + 1);
}

/** Прогнать запись целиком: снимки, номера ходов, начало (после подготовки) и конец партии */
function build(rec: ViewRecord, version: number, from: number, switchAt: number, seat: PlayerId, name: string): Pick<Replay, 'turns' | 'snaps' | 'first' | 'last'> {
  const v = { rec, version, from, switchAt };
  const G = createGame({ seed: rec.seed, players: recPlayers(rec), ...gameFlags(rec), rulesVersion: from });
  // за столом проигравший — под именем из профиля (в старых записях он мог быть «Игроком 5»)
  const own = G.players[seat];
  if (own && name) own.name = name;
  const snaps = new Map<number, Game>([[0, snap(G)]]);
  const turns = [G.turnNo ?? 0];
  let first = G.phase === 'play' ? 0 : -1;
  const n = rec.journal.length;
  for (let i = 0; i < n; i++) {
    step(v, G, i);
    const pos = i + 1;
    turns.push(G.turnNo ?? 0);
    if (first < 0 && G.phase === 'play') first = pos;
    if (pos % SNAP_EVERY === 0) snaps.set(pos, snap(G));
  }
  if (first < 0) throw new Error('партия не дошла до игры');
  // последняя позиция — стол перед финальным действием: итог показывает окно конца партии
  const last = G.phase === 'ended' ? n - 1 : n;
  return { turns, snaps, first, last: Math.max(first, last) };
}

/** Встать на позицию pos (реплей от ближайшего снимка); анимаций нет — это перемотка */
function seek(r: Replay, pos: number): void {
  pos = Math.max(r.first, Math.min(r.last, pos));
  let base = 0;
  for (const k of r.snaps.keys()) if (k <= pos && k > base) base = k;
  const G = snap(r.snaps.get(base)!);
  applyRebalance(!!G.rebalance, rv(G));
  // счётчик id карт в движке общий: новые карты реплея должны получить те же id, что в записи
  setIidCounter(maxIid(G) + 1);
  for (let i = base; i < pos; i++) step(r, G, i);
  r.G = G;
  r.pos = pos;
  st.G = G;
  ui.lastSeq = G.eventSeq;
  ui.raised = null;
}

/** Одно действие вперёд — с анимацией событий (как ход бота) */
function forward(r: Replay): void {
  step(r, r.G, r.pos);
  r.pos++;
  if (r.pos % SNAP_EVERY === 0 && !r.snaps.has(r.pos)) r.snaps.set(r.pos, snap(r.G));
}

function stopTimer(r: Replay): void {
  if (r.timer) clearTimeout(r.timer);
  r.timer = null;
}

function pause(r: Replay): void {
  r.playing = false;
  stopTimer(r);
}

function schedule(r: Replay): void {
  stopTimer(r);
  if (!r.playing) return;
  const ms = (STEP_MS * (r.G.prompt ? 1.25 : 1)) / r.speed;
  r.timer = setTimeout(() => tick(r), ms);
}

function tick(r: Replay): void {
  r.timer = null;
  if (R !== r || !r.playing) return;
  // поверх стола открыто окно (карта крупно, журнал) — ждём, пока закроют
  if (modalOpen()) {
    schedule(r);
    return;
  }
  if (r.pos >= r.last) {
    pause(r);
    render();
    showEnd(r);
    return;
  }
  if (!guarded(() => forward(r))) return;
  render();
  schedule(r);
}

/** Реплей споткнулся (запись не сходится с движком) — выйти с сообщением, а не оставить сломанный стол */
function guarded(fn: () => void): boolean {
  try {
    fn();
    return true;
  } catch (e) {
    exitReplay();
    toast(`⚠ Партию дальше не воспроизвести: ${e instanceof Error ? e.message : String(e)}`, 5000);
    return false;
  }
}

function showEnd(r: Replay): void {
  openModal(r.endBuild);
}

/** Разбор решений проигравшего — движковый reviewGame (несколько секунд): сначала подпись, потом расчёт */
function runReview(r: Replay): void {
  const e = r.end;
  if (e.review || e.reviewBusy) return;
  e.reviewBusy = true;
  e.error = '';
  repaintEnd(r);
  setTimeout(() => {
    try {
      e.review = reviewGame({ seed: r.rec.seed, players: recPlayers(r.rec), journal: r.rec.journal, ...gameFlags(r.rec), rulesVersion: r.version }, [e.seat]);
    } catch (x) {
      e.error = `Разбор не удался: ${x instanceof Error ? x.message : String(x)}`;
    } finally {
      e.reviewBusy = false;
      // разбор создавал свою партию — правила обратно к записи на экране
      if (R === r) applyRebalance(!!r.G.rebalance, rv(r.G));
      repaintEnd(r);
    }
  }, 40);
}

function repaintEnd(r: Replay): void {
  if (R === r && modalOpen() && currentModal() === r.endBuild) repaintModal();
}

/** Открыть проигранную партию в плеере: загрузка записи, прогон, стол на начале партии — и сразу воспроизведение */
export async function startReplay(loss: PlayedGame, who: string, me: boolean): Promise<void> {
  if (st.net) {
    toast('Смотреть партии можно вне онлайн-комнаты');
    return;
  }
  const my = ++opening;
  toast('Загружаю партию…', 1500);
  let rec: ViewRecord;
  try {
    const res = await fetch(`api/player/game?id=${loss.game}`);
    const j: unknown = res.ok ? await res.json() : null;
    const x = j as ViewRecord | null;
    if (!x || !Array.isArray(x.journal) || !Array.isArray(x.players) || typeof x.seed !== 'number') throw new Error('запись партии не найдена');
    rec = x;
  } catch (e) {
    if (my === opening) toast(e instanceof TypeError ? '⚠ Нет связи с сервером' : `⚠ ${e instanceof Error ? e.message : String(e)}`, 4000);
    return;
  }
  if (my !== opening || ui.replay) return;
  // тяжёлая синхронная часть (прогон всей партии) — после того, как ушла подпись «загружаю»
  await new Promise((res) => setTimeout(res, 30));
  if (my !== opening || ui.replay) return;
  const seat = loss.seat >= 0 && loss.seat < rec.players.length ? loss.seat : Math.max(0, rec.players.findIndex((p) => !p.bot));
  let parts: Pick<Replay, 'turns' | 'snaps' | 'first' | 'last'>;
  let version = rec.rulesVersion ?? RULES_VERSION;
  let from = version;
  let switchAt = 0;
  try {
    try {
      if (rec.rulesVersion == null) throw new Error('версия не записана');
      parts = build(rec, version, from, switchAt, seat, who);
    } catch {
      // версия не записана или запись на ней не сошлась — подбор версии (и смены посреди партии) реплеем
      const out = replayRecordOutcome(rec);
      if (!out.ok) throw new Error(out.error || 'запись не воспроизводится');
      version = out.version;
      from = out.switched?.from ?? version;
      switchAt = out.switched?.at ?? 0;
      parts = build(rec, version, from, switchAt, seat, who);
    }
  } catch (e) {
    restoreRules();
    toast(`⚠ Партию не удалось воспроизвести: ${e instanceof Error ? e.message : String(e)}`, 5000);
    return;
  }
  const end: ReplayEnd = { game: loss, who, me, seat, review: null, reviewBusy: false, error: '' };
  const r: Replay = {
    rec, end, version, from, switchAt, ...parts,
    G: parts.snaps.get(0)!, pos: 0, playing: true, speed: 1, timer: null,
    endBuild: () => OV.replayEndV(end),
    back: currentModal(),
    prev: { G: st.G, viewer: ui.viewer, lastSeq: ui.lastSeq, mode: ui.mode, turnSeq: ui.turnSeq },
  };
  R = r;
  if (ui.botTimer) clearTimeout(ui.botTimer);
  ui.botTimer = null;
  closeModal();
  ui.replay = true;
  ui.viewer = seat;
  ui.mode = 'play';
  ui.zoom = null;
  ui.handOpen = false;
  ui.turnSeq = null;
  seek(r, r.first);
  render();
  schedule(r);
}

/** Выйти из плеера: прежняя партия и окно, из которого открыли */
export function exitReplay(): void {
  const r = R;
  if (!r) return;
  stopTimer(r);
  R = null;
  opening++;
  closeModal();
  ui.replay = false;
  st.G = r.prev.G;
  ui.viewer = r.prev.viewer;
  ui.lastSeq = r.prev.lastSeq;
  ui.mode = r.prev.mode;
  ui.turnSeq = r.prev.turnSeq;
  ui.raised = null;
  ui.zoom = null;
  restoreRules();
  render();
  if (r.back) openModal(r.back);
}

export function replayCmd(op: ReplayOp): void {
  const r = R;
  if (!r) return;
  switch (op) {
    case 'exit':
      exitReplay();
      return;
    case 'toggle':
      if (r.playing) pause(r);
      else {
        if (r.pos >= r.last) seek(r, r.first);
        r.playing = true;
        schedule(r);
      }
      render();
      return;
    case 'next':
      pause(r);
      if (r.pos >= r.last) {
        render();
        showEnd(r);
        return;
      }
      if (guarded(() => forward(r))) render();
      return;
    case 'prev':
      pause(r);
      seek(r, r.pos - 1);
      render();
      return;
    case 'prevTurn': {
      pause(r);
      // к началу этого хода; уже в начале — к началу предыдущего
      const startOf = (t: number): number => {
        let p = r.first;
        while (p < r.last && (r.turns[p] ?? 0) < t) p++;
        return p;
      };
      const t = r.turns[r.pos] ?? 0;
      let p = startOf(t);
      if (p >= r.pos) p = startOf(t - 1);
      seek(r, Math.min(p, r.pos - 1));
      render();
      return;
    }
    case 'nextTurn': {
      pause(r);
      const t = r.turns[r.pos] ?? 0;
      let p = r.pos + 1;
      while (p < r.last && (r.turns[p] ?? 0) <= t) p++;
      if (r.pos >= r.last) {
        render();
        showEnd(r);
        return;
      }
      seek(r, p);
      render();
      return;
    }
    case 'speed': {
      const i = SPEEDS.indexOf(r.speed as (typeof SPEEDS)[number]);
      r.speed = SPEEDS[(i + 1) % SPEEDS.length] ?? 1;
      schedule(r);
      render();
      return;
    }
    case 'log':
      showLog();
      return;
    case 'restart':
      closeModal();
      seek(r, r.first);
      r.playing = true;
      render();
      schedule(r);
      return;
    case 'review':
      runReview(r);
      return;
  }
}

/** К решению из разбора: стол за миг до действия №idx журнала, на паузе */
export function replayAt(idx: number): void {
  const r = R;
  if (!r) return;
  closeModal();
  pause(r);
  seek(r, idx);
  render();
}

/** Ползунок плеера: перемотка на позицию (пока тянут — пауза) */
export function initReplay(): void {
  if (typeof document === 'undefined') return;
  document.addEventListener('input', (e) => {
    const t = e.target;
    const r = R;
    if (!r || !(t instanceof HTMLInputElement) || t.id !== 'replay-seek') return;
    pause(r);
    seek(r, Number(t.value));
    render();
  });
}
